import { useState, useEffect } from 'react'

const _a = btoa('https://g649289pzb.execute-api.us-east-1.amazonaws.com/default/NightHash_EmailForwarder')
const _e = () => atob(_a)

type FormState = 'idle' | 'loading' | 'success' | 'error'

function App() {
  const [dots, setDots] = useState('')
  const [email, setEmail] = useState('')
  const [formState, setFormState] = useState<FormState>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(d => d.length >= 3 ? '' : d + '.')
    }, 500)
    return () => clearInterval(interval)
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return

    setFormState('loading')
    setErrorMsg('')

    try {
      const res = await fetch(_e(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })

      const data = await res.json()

      if (res.ok) {
        setFormState('success')
        setEmail('')
      } else {
        setFormState('error')
        setErrorMsg(data.error || 'Something went wrong.')
      }
    } catch {
      setFormState('error')
      setErrorMsg('Connection failed. Try again.')
    }
  }

  return (
    <div className="min-h-screen bg-cyber-black text-white font-body overflow-hidden relative">
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-neon-blue/8 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-cyber-purple-start/8 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }}></div>
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `linear-gradient(rgba(78, 240, 244, 0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(78, 240, 244, 0.5) 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}></div>
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-neon-blue/40 to-transparent animate-scan"></div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 py-16">

        {/* Logo */}
        <div className="mb-10">
          <div className="flex items-center gap-3 group">
            <div className="relative">
              <div className="w-12 h-12 sm:w-14 sm:h-14 border-2 border-neon-blue/60 rounded-lg flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 bg-neon-blue/10 group-hover:bg-neon-blue/20 transition-colors"></div>
                <span className="text-neon-blue font-header text-xl sm:text-2xl font-bold relative z-10">N</span>
              </div>
              <div className="absolute -inset-2 border border-neon-blue/20 rounded-lg animate-pulse"></div>
            </div>
            <span className="font-header text-xl sm:text-2xl font-bold tracking-[0.2em]">NIGHTHASH</span>
          </div>
        </div>

        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cyber-purple-start/30 bg-cyber-purple-start/5 text-cyber-purple-start text-sm mb-6 text-center">
          <span className="w-2 h-2 bg-cyber-purple-start rounded-full animate-pulse shrink-0"></span>
          Something Dangerous Is Brewing
        </div>

        {/* Heading */}
        <h1 className="text-5xl sm:text-6xl md:text-8xl font-header font-black mb-4 tracking-tight text-center">
          <span className="bg-gradient-to-r from-white via-neon-blue to-white bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
            COMING
          </span>
          <br />
          <span className="bg-gradient-to-r from-neon-blue via-cyber-purple-start to-neon-blue bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
            SOON
          </span>
        </h1>

        {/* Tagline */}
        <div className="mb-10 text-center px-2">
          <p className="text-lg sm:text-xl md:text-2xl text-gray-400 font-light max-w-xl">
            We're building the <span className="text-neon-blue font-medium">last security company</span> you'll ever need.
            <span className="inline-block w-1 h-5 bg-neon-blue ml-1 animate-blink"></span>
          </p>
          <p className="text-gray-500 mt-3 text-sm">
            Full-spectrum protection for those who refuse to be breached.
          </p>
        </div>

        {/* Email Capture */}
        <div className="w-full max-w-md px-0">
          {formState === 'success' ? (
            <div className="text-center p-6 border border-neon-blue/30 bg-neon-blue/5 rounded-xl">
              <div className="text-4xl mb-3">🎯</div>
              <p className="text-neon-blue font-medium">You're on the list.</p>
              <p className="text-gray-500 text-sm mt-1">We'll notify you the moment we go live.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email for early access"
                required
                disabled={formState === 'loading'}
                className="w-full px-5 py-4 bg-cyber-dark/80 border border-gray-700 rounded-xl text-white placeholder-gray-500 text-sm focus:border-neon-blue focus:outline-none focus:ring-1 focus:ring-neon-blue/50 transition-all backdrop-blur disabled:opacity-50"
              />

              {formState === 'error' && (
                <p className="text-red-400 text-sm text-center">{errorMsg}</p>
              )}

              <button
                type="submit"
                disabled={formState === 'loading'}
                className="w-full bg-gradient-to-r from-cyber-purple-start via-cyber-purple-end to-cyber-purple-start bg-[length:200%_auto] px-8 py-4 rounded-xl font-medium text-white hover:opacity-90 transition-all hover:scale-[1.02] animate-gradient disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {formState === 'loading' ? 'Sending...' : 'Get Early Access'}
              </button>
            </form>
          )}

          <p className="text-gray-600 text-xs text-center mt-4">
            Join 2,400+ security-conscious leaders on the waitlist
          </p>
        </div>

        {/* Contact — single row, no wrapping */}
        <div className="mt-12 flex flex-row items-center justify-center gap-6 flex-wrap">
          <a
            href="mailto:security@nighthash.com"
            className="flex items-center gap-2 text-gray-500 hover:text-neon-blue transition-colors whitespace-nowrap"
          >
            <span>📧</span>
            <span className="text-sm">security@nighthash.com</span>
          </a>
          <a
            href="tel:+16174688995"
            className="flex items-center gap-2 text-gray-500 hover:text-neon-blue transition-colors whitespace-nowrap"
          >
            <span>📱</span>
            <span className="text-sm">+1 (617) 468-8995</span>
          </a>
        </div>

        {/* Status */}
        <div className="mt-10">
          <div className="flex items-center gap-2 text-gray-600 text-xs">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            <span>Systems initializing{dots}</span>
          </div>
        </div>
      </div>

      {/* Corner Elements */}
      <div className="absolute top-4 left-4 w-10 h-10 sm:w-16 sm:h-16 border-l-2 border-t-2 border-neon-blue/30"></div>
      <div className="absolute top-4 right-4 w-10 h-10 sm:w-16 sm:h-16 border-r-2 border-t-2 border-neon-blue/30"></div>
      <div className="absolute bottom-4 left-4 w-10 h-10 sm:w-16 sm:h-16 border-l-2 border-b-2 border-neon-blue/30"></div>
      <div className="absolute bottom-4 right-4 w-10 h-10 sm:w-16 sm:h-16 border-r-2 border-b-2 border-neon-blue/30"></div>
    </div>
  )
}

export default App
